import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Sparkles, Layers, Building2, TrendingUp, Wrench, BookOpen, ShieldCheck, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { notify } from "../lib/notify";
import CSRSGauge from "../components/CSRSGauge";
import SEO from "../components/SEO";

const HERO_STATS = [
  { k: "32+", v: "Risk frameworks" },
  { k: "8", v: "Singapore regulator hubs" },
  { k: "12", v: "Pre-built workflows" },
  { k: "300+", v: "Glossary terms" },
];

const FRAMEWORK_PILLS = ["ISO 31000", "COSO ERM", "NIST RMF", "ISO 27001", "MAS TRM", "MAS 655", "PDPA", "Basel III/IV", "Solvency II", "DORA", "NIS2", "SOC 2", "COBIT", "Veritas FEAT", "MindForge", "TCFD"];

export default function Home({ onOpenAdvisor }) {
  const [sneer, setSneer] = useState(null);
  const [flow, setFlow] = useState(null);
  const [trends, setTrends] = useState(null);

  useEffect(() => {
    api.get("/sneer").then(r => setSneer(r.data)).catch(() => {});
    api.get("/money-flow").then(r => setFlow(r.data)).catch(() => {});
    api.get("/trends/sg").then(r => setTrends(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="page-home">
      <SEO
        title="Singapore Risk & Compliance Encyclopedia · MAS · PDPA · CCoP 2.0"
        description="The Singapore-specific risk encyclopedia for CROs, DPOs and GRC consultants. 32 frameworks, 8 regulator hubs, 12 workflow templates, live S$NEER, CSRS score, MAS TRM scorer, PDPA scorer, AI / Veritas FEAT, MAS deadline calendar. Free."
        keywords={["Singapore risk", "MAS TRM", "PDPA Singapore", "CCoP 2.0", "MAS MindForge", "Veritas FEAT", "CSRS", "S$NEER", "GRC Singapore", "compliance Singapore", "DPO Singapore", "MAS Notice 655"]}
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "RiskPedia Singapore",
          url: "https://sg-riskguide.preview.emergentagent.com",
          description: "Singapore's risk and compliance encyclopedia — MAS, PDPC, CSA frameworks, workflow templates and live regulatory intelligence.",
          areaServed: { "@type": "Country", name: "Singapore" },
        }}
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="bg-grid absolute inset-0 opacity-60" />
        <div className="container-rp pt-16 pb-20 lg:pt-24 lg:pb-28 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-2 fade-in">
                <span className="pulse-dot" />
                <span className="overline">Singapore Edition · Jan 2026</span>
              </div>
              <h1 className="font-display mt-6 text-5xl md:text-6xl lg:text-[72px] leading-[0.95] font-bold tracking-tight text-slate-900 fade-in">
                Match the right
                <br />
                risk framework.
                <br />
                <span style={{ color: "var(--rp-accent)" }}>For Singapore.</span>
              </h1>
              <p className="mt-8 text-lg text-slate-600 max-w-xl leading-relaxed fade-in-delayed">
                The Risk Encyclopedia, localised. MAS TRM, PDPA, MindForge, Veritas FEAT — translated into operating playbooks for CROs, internal auditors and GRC consultants across Singapore BFSI.
              </p>
              <div className="mt-10 flex flex-wrap gap-3 fade-in-delayed">
                <Link to="/tools/framework-matcher" className="btn-accent" data-testid="hero-cta-matcher">
                  <Sparkles size={14} /> 5-question framework matcher
                </Link>
                <Link to="/frameworks" className="btn-outline" data-testid="hero-cta-library">
                  Browse the library <ArrowRight size={14} />
                </Link>
              </div>

              {/* Pills */}
              <div className="mt-12">
                <div className="overline-muted mb-3">A curated index</div>
                <div className="flex flex-wrap gap-2">
                  {FRAMEWORK_PILLS.map(p => (
                    <span key={p} className="text-xs font-mono px-2.5 py-1 border border-slate-200 rounded-sm text-slate-700 bg-white">{p}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Right column: CSRS preview */}
            <div className="lg:col-span-5">
              <div className="rp-card p-6 lg:p-8 fade-in-delayed">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="overline">Flagship metric</div>
                    <h3 className="font-display font-semibold text-xl mt-2">Composite Singapore Risk Score</h3>
                  </div>
                  <span className="text-[10px] font-mono text-slate-400">CSRS · v1.0</span>
                </div>
                <div className="mt-6 flex justify-center">
                  <CSRSGauge score={71} band="Adequate" color="amber" size={300} />
                </div>
                <div className="mt-4 text-xs text-slate-500 leading-relaxed">
                  Combines MAS TRM, PDPA, MindForge AI, third-party risk, cyber hygiene and operational resilience into one 0–100 metric.
                </div>
                <Link to="/tools/csrs-calculator" className="mt-5 inline-flex items-center gap-1 text-sm font-medium" style={{ color: "var(--rp-accent)" }} data-testid="hero-cta-csrs">
                  Score my organisation <ArrowRight size={14} />
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 mt-6 border-t border-l border-slate-200">
                {HERO_STATS.map(s => (
                  <div key={s.v} className="px-5 py-4 border-r border-b border-slate-200 bg-white">
                    <div className="font-display text-3xl font-bold" style={{ color: "var(--rp-primary)" }}>{s.k}</div>
                    <div className="text-xs text-slate-500 mt-1">{s.v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ticker stripe */}
      <section className="border-y border-slate-200 force-dark overflow-hidden">
        <div className="py-3 ticker-track">
          <div className="flex items-center gap-12 px-12 text-xs font-mono whitespace-nowrap">
            {Array.from({ length: 2 }).flatMap((_, j) => [
              <span key={`a${j}`} className="flex items-center gap-2"><span className="pulse-dot" /> MAS MPS · Slope unchanged, gradual SGD appreciation</span>,
              <span key={`b${j}`}>PDPC · Mandatory breach notification: 72hrs</span>,
              <span key={`c${j}`}>MAS Notice 655 · Six cyber-hygiene baselines mandatory for all FIs</span>,
              <span key={`d${j}`}>MindForge · Veritas FEAT operational toolkit live</span>,
              <span key={`e${j}`}>UPI ⇄ PayNow · Daily cap S$1,000</span>,
              <span key={`f${j}`}>CSA · CCoP 2.0 effective</span>,
              <span key={`g${j}`}>RBC 2 · Insurance capital regime active</span>,
            ])}
          </div>
        </div>
      </section>

      {/* Workflows + Trends section */}
      <section className="section border-b border-slate-200">
        <div className="container-rp">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-7">
              <div className="overline">Automation · save · share</div>
              <h2 className="font-display text-4xl lg:text-5xl font-semibold mt-3 tracking-tight">12 pre-built workflows.<br /><span style={{ color: "var(--rp-accent)" }}>Run in minutes, not weeks.</span></h2>
              <p className="text-slate-600 mt-5 max-w-xl leading-relaxed">MAS TRM quarterly review · PDPA annual audit · CSRS monthly pulse · AI/Veritas FEAT readiness · Vendor onboarding · Incident 72-hour response · CCoP 2.0 attestation · BCP drill · Cross-border data transfer · Weekly Regulatory Pulse · AML self-check · Framework Overlap Mapper. Pick one, save to your profile, schedule it. Each run produces a Claude-written executive brief you can share.</p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/workflows" className="btn-accent" data-testid="home-cta-workflows">Browse 12 workflows <ArrowRight size={14} /></Link>
                <Link to="/newsletter" className="btn-outline" data-testid="home-cta-newsletter">Read the weekly briefing</Link>
              </div>
            </div>
            <aside className="lg:col-span-5">
              <div className="rp-card p-6" data-testid="home-trends-card">
                <div className="flex items-center justify-between">
                  <div className="overline flex items-center gap-2"><TrendingUp size={11} /> SG search trends · live</div>
                  <Link to="/trending" className="text-[10px] font-mono inline-flex items-center gap-0.5" style={{ color: "var(--rp-accent)" }}>All 15 <ArrowUpRight size={10} /></Link>
                </div>
                <h3 className="font-display text-xl font-semibold mt-3">What Singapore is searching for.</h3>
                <div className="mt-5 space-y-2">
                  {(trends?.trends || []).slice(0, 6).map((t, i) => (
                    <div key={t.keyword} className="flex items-center gap-3 py-1.5">
                      <span className="font-mono text-[10px] text-slate-400 w-5">{(i + 1).toString().padStart(2, "0")}</span>
                      <span className="flex-1 text-sm">{t.keyword}</span>
                      <span className="font-mono text-xs" style={{ color: "var(--rp-accent)" }}>{t.latest_score}</span>
                    </div>
                  ))}
                  {!trends && <div className="text-xs text-slate-400">Loading…</div>}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Six core sections */}
      <section className="section">
        <div className="container-rp">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-14">
            <div className="lg:col-span-8">
              <div className="overline">One platform · six destinations</div>
              <h2 className="font-display text-4xl lg:text-5xl font-semibold mt-3 tracking-tight">
                Group by what you actually need to do.
              </h2>
            </div>
            <p className="lg:col-span-4 text-slate-600">The top menu groups every page into six clean destinations. Pick your path; we'll keep you out of the weeds.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {[
              { icon: Layers, label: "Frameworks", desc: "32+ frameworks with side-by-side comparison and certification tracks.", to: "/frameworks", cta: "Browse library" },
              { icon: Building2, label: "Singapore Hub", desc: "MAS TRM · PDPA · CSA · MindForge — every regulator, every control.", to: "/singapore", cta: "Open hub" },
              { icon: TrendingUp, label: "Money Flow", desc: "S$NEER tracker · MAS MPS · UPI–PayNow · Project Nexus.", to: "/money-flow", cta: "View intel" },
              { icon: Wrench, label: "Tools", desc: "CSRS scorer · PDPA readiness · MAS TRM heat-map · AI maturity.", to: "/tools", cta: "Run a scorer" },
              { icon: BookOpen, label: "Resources", desc: "Glossary · webinars · shorts · weekly newsletter.", to: "/glossary", cta: "Open glossary" },
              { icon: ShieldCheck, label: "About", desc: "Why RiskPedia exists, our partners, and how to contact us.", to: "/about", cta: "Read more" },
            ].map(({ icon: Icon, label, desc, to, cta }) => (
              <Link to={to} key={label} className="bg-white p-8 hover:bg-slate-50 transition-colors group" data-testid={`section-card-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                <Icon size={22} style={{ color: "var(--rp-primary)" }} />
                <div className="mt-6 font-display text-2xl font-semibold tracking-tight">{label}</div>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{desc}</p>
                <div className="mt-6 flex items-center gap-1 text-xs font-medium" style={{ color: "var(--rp-accent)" }}>
                  {cta} <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Singapore Hub spotlight */}
      <HomeSingaporeSpotlight />

      {/* Money Flow live snippet */}
      <HomeMoneyFlow sneer={sneer} flow={flow} />

      {/* AI Advisor CTA */}
      <HomeAdvisorCta onOpenAdvisor={onOpenAdvisor} />

      {/* Newsletter */}
      <HomeNewsletter />
    </div>
  );
}

function HomeSingaporeSpotlight() {
  return (
      <section className="section bg-slate-50 border-y border-slate-200">
        <div className="container-rp">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="overline">Singapore Regulatory Hub</div>
              <h2 className="font-display text-4xl lg:text-5xl font-semibold mt-3 tracking-tight">
                Eight regulators.<br />
                One operating manual.
              </h2>
              <p className="text-slate-600 mt-6 leading-relaxed">
                Deep-dive control catalogues, applicability matrices, compliance calendars and penalty frameworks for MAS, PDPC, CSA and every Singapore BFSI rulebook.
              </p>
              <div className="mt-8 flex gap-3 flex-wrap">
                <Link to="/singapore" className="btn-primary">Explore Singapore Hub <ChevronRight size={14} /></Link>
                <Link to="/singapore/breach-playbook" className="btn-outline">Multi-regulator breach playbook</Link>
              </div>
            </div>
            <div className="lg:col-span-7 grid grid-cols-2 gap-4">
              {[
                { name: "MAS TRM", desc: "9 control domains", to: "/singapore/mas-trm", tag: "Tech Risk" },
                { name: "MAS Notice 655", desc: "6 cyber-hygiene baselines", to: "/singapore/mas-notice-655", tag: "Cyber" },
                { name: "PDPA 2012", desc: "9 obligations · 72hr breach", to: "/singapore/pdpa-2012", tag: "Privacy" },
                { name: "Cybersecurity Act", desc: "CII · CCoP 2.0", to: "/singapore/cybersecurity-act", tag: "Cyber" },
                { name: "MindForge", desc: "AI risk consortium", to: "/singapore/mindforge", tag: "AI Risk" },
                { name: "Payment Services Act", desc: "PSA · DPT licensing", to: "/singapore/payment-services-act", tag: "Payments" },
              ].map(r => (
                <Link to={r.to} key={r.name} className="rp-card p-5">
                  <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "var(--rp-accent)" }}>{r.tag}</div>
                  <div className="font-display text-lg font-semibold mt-2">{r.name}</div>
                  <div className="text-xs text-slate-500 mt-1">{r.desc}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
  );
}

function HomeMoneyFlow({ sneer, flow }) {
  return (
      <section className="section">
        <div className="container-rp">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="overline">Money Flow Intelligence</div>
              <h2 className="font-display text-4xl lg:text-5xl font-semibold mt-3 tracking-tight">Live signals — straight from the corridor.</h2>
            </div>
            <Link to="/money-flow" className="btn-outline">Open Money Flow Hub <ArrowRight size={14} /></Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* S$NEER */}
            <div className="rp-card p-6 lg:col-span-2">
              <div className="flex items-center justify-between">
                <div className="overline">S$NEER · MAS policy band</div>
                <span className="text-[10px] font-mono text-slate-400">{sneer?.next_mps ? `Next MPS · ${sneer.next_mps}` : "Loading..."}</span>
              </div>
              <div className="mt-4 flex items-baseline gap-4">
                <div className="font-display text-5xl font-bold" style={{ color: "var(--rp-primary)" }}>{sneer ? sneer.current.toFixed(2) : "—"}</div>
                {sneer && (
                  <div className={`text-sm font-mono ${sneer.change_30d_bps >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                    {sneer.change_30d_bps >= 0 ? "+" : ""}{sneer.change_30d_bps} bps · 30d
                  </div>
                )}
              </div>
              {sneer && <p className="text-sm text-slate-600 mt-4 leading-relaxed">{sneer.mas_stance}</p>}
              {sneer && (
                <div className="mt-6 grid grid-cols-30 gap-px h-16 items-end">
                  {sneer.series.map((p, i) => {
                    const min = Math.min(...sneer.series.map(x => x.value));
                    const max = Math.max(...sneer.series.map(x => x.value));
                    const range = Math.max(0.001, max - min);
                    const h = 6 + ((p.value - min) / range) * 56;
                    return <div key={p.date} style={{ height: `${h}px`, background: "var(--rp-primary)", opacity: 0.4 + (i / sneer.series.length) * 0.6 }} />;
                  })}
                </div>
              )}
            </div>

            {/* Corridors */}
            <div className="rp-card p-6">
              <div className="overline">Top corridors · SGD mn</div>
              <div className="mt-4 space-y-3">
                {flow?.corridors?.slice(0, 5).map(c => (
                  <div key={c.name} className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">{c.name}</div>
                      <div className="text-[10px] text-slate-400 font-mono">{c.status}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm font-semibold">S${c.volume_sgd_m}m</div>
                      <div className="text-[10px] text-emerald-600 font-mono">+{c.growth_yoy_pct}% YoY</div>
                    </div>
                  </div>
                ))}
                {!flow && <div className="text-xs text-slate-400">Loading…</div>}
              </div>
            </div>
          </div>
        </div>
      </section>
  );
}

function HomeAdvisorCta({ onOpenAdvisor }) {
  return (
      <section className="section force-dark relative overflow-hidden">
        <div className="bg-grid-dark absolute inset-0 opacity-60" />
        <div className="container-rp relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="overline">AI Advisor · Powered by Claude Sonnet 4.5</div>
              <h2 className="font-display text-4xl lg:text-5xl font-semibold mt-3 tracking-tight">
                Four advisors. One panel.<br />
                Singapore-aware answers.
              </h2>
              <p className="text-slate-300 mt-6 max-w-xl leading-relaxed">
                Switch between <strong className="text-white">Kai</strong> (general GRC), <strong className="text-white">Priya S.</strong> (PDPA), <strong className="text-white">Rajan</strong> (Money Flow) and <strong className="text-white">Mei</strong> (MAS Regulations). Multi-turn, persona-aware, with disclaimers built-in.
              </p>
              <div className="mt-8">
                <button onClick={onOpenAdvisor} className="btn-accent" data-testid="home-cta-advisor">
                  <Sparkles size={14} /> Open AI Advisor
                </button>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { name: "Kai", role: "General GRC", color: "bg-slate-700" },
                  { name: "Priya S.", role: "PDPA Expert", color: "bg-blue-700" },
                  { name: "Rajan", role: "Money Flow", color: "bg-emerald-700" },
                  { name: "Mei", role: "MAS Regs", color: "bg-rose-700" },
                ].map(p => (
                  <div key={p.name} className="bg-slate-800/60 border border-slate-700 rounded-sm p-5">
                    <div className={`${p.color} w-9 h-9 rounded-full flex items-center justify-center font-semibold`}>{p.name[0]}</div>
                    <div className="font-display font-semibold mt-3">{p.name}</div>
                    <div className="text-xs text-slate-400">{p.role}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
  );
}

function HomeNewsletter() {
  return (
      <section className="section">
        <div className="container-rp">
          <div className="rp-card p-10 lg:p-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="overline">Singapore Risk Briefing</div>
              <h3 className="font-display text-3xl font-semibold mt-3">Every Monday. Free. Unsubscribe anytime.</h3>
              <p className="text-slate-600 mt-3">A 4-minute read on the week ahead — MAS, PDPC and CSA developments distilled for SG BFSI professionals.</p>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const fd = new FormData(form);
              try {
                await api.post("/newsletter", { email: fd.get("email"), role: fd.get("role") });
                form.reset();
                notify.success("You're on the list. See you Monday.");
              } catch { notify.error("Could not subscribe. Please try again."); }
            }} className="lg:col-span-5 flex flex-col gap-3" data-testid="newsletter-form">
              <input name="email" type="email" required placeholder="work-email@bank.sg" className="border border-slate-200 rounded-sm px-4 py-3 text-sm focus:outline-none focus:border-slate-400" data-testid="newsletter-email" />
              <select name="role" className="border border-slate-200 rounded-sm px-4 py-3 text-sm bg-white" data-testid="newsletter-role">
                <option value="">Your role (optional)</option>
                <option>Risk Manager / CRO</option>
                <option>GRC Consultant</option>
                <option>Internal Auditor</option>
                <option>Sales / BD</option>
                <option>Other</option>
              </select>
              <button className="btn-primary justify-center" data-testid="newsletter-submit">Subscribe free</button>
            </form>
          </div>
        </div>
      </section>
  );
}
